import { Discount } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Button, Link as MUILink, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plan, ScenarioPlanName, type ScenarioPlan, type UserQuery } from '../api/gql';
import { getConfig } from '../utils/config';
import { getPriceWithCurrencyString } from '../utils/currencies';
import { formatPhoneNumber, formatPrice } from '../utils/format';
import { notify } from '../utils/notification';
import { PlanCard } from './PlanCard';

const { salesPhone, salesEmail } = getConfig();
const TRANS_NS = 'billing';

type Props = {
  user?: UserQuery['user'];
  onPlanChoose: (plan: Plan) => void;
  standardPrice: number;
  scenarioPlan?: ScenarioPlan;
  standardDiscount?: number;
  contact?: {
    missingEmail?: boolean;
    missingPhone?: boolean;
  };
  isTrialPlanDisplayed?: boolean;
  remindingsLeft?: number;
  isLoading?: boolean;
  transNs?: string;
};

export const BillingPlans = ({
  user,
  scenarioPlan = { email: 3, sms: 3, call: 1, name: ScenarioPlanName.Plan_331 },
  transNs = TRANS_NS,
  isTrialPlanDisplayed = true,
  remindingsLeft = 0,
  ...props
}: Props) => {
  const { t } = useTranslation(transNs);

  const { businessName, streetAddress, city, zipCode, country } = user || {};

  const userHasRequiredBillingInfo = !!businessName && !!streetAddress && !!city && !!zipCode && !!country;

  const handleContactSales = () => {
    if (!userHasRequiredBillingInfo) {
      notify.negative({
        message: t('plan.missingBillingInfo'),
      });

      return;
    }

    window.location.href = `mailto:${salesEmail}`;
  };

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
      {!isTrialPlanDisplayed && (
        <Box mb={6}>
          <Alert
            severity={'info'}
            sx={{
              background: 'none',
              boxShadow: 'none',
              border: 'none',
              p: 0,
              fontSize: '1.5rem',
              '& .MuiAlert-icon': {
                fontSize: '2.5rem',
              },
            }}
          >
            {t('batchAlert')}
          </Alert>
        </Box>
      )}
      <Grid2 container spacing={4} alignItems={'stretch'}>
        {isTrialPlanDisplayed ? (
          <Grid2 xs={12} lg={4}>
            <PlanCard
              title={t('plan.first.title')}
              subtitle={t('plan.first.subtitle')}
              countOfInvoices={5}
              countOfInvoicesText={t('countOfInvoicesText')}
              plansHeading={t('planIncludes')}
              noEmails={3}
              emailsDescription={t('plan.first.emailInfo', {
                count: scenarioPlan.email,
              })}
              noSms={'2-3'}
              smsDescription={t('plan.first.smsInfo')}
              noCalls={0}
              callsDescription={t('plan.first.phoneInfo')}
              endOfRemindingDescription={t('plan.first.endOfRemindingInfo')}
              disableSms={props.contact?.missingPhone}
              disableEmail={props.contact?.missingEmail}
              disableCalls={props.contact?.missingPhone}
              priceForUnit={getPriceWithCurrencyString(0, 'czk')}
              actionContent={
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  width={'100%'}
                  py={2}
                >
                  {remindingsLeft > 0 && (
                    <Alert severity={'info'} sx={{ mb: 4 }}>
                      {t('remindingsLeft', { count: remindingsLeft })}
                    </Alert>
                  )}
                  <LoadingButton
                    loading={props.isLoading}
                    disabled={remindingsLeft === 0}
                    size={'large'}
                    variant={'outlined'}
                    onClick={() => props.onPlanChoose(Plan.Trial)}
                  >
                    {remindingsLeft > 0 ? t('plan.first.action') : t('remindingNotAvailable.heading')}
                  </LoadingButton>
                  <Typography mt={1} variant={'caption'} color={'textSecondary'} textAlign={'center'}>
                    {remindingsLeft > 0 ? t('plan.first.overline') : t('remindingNotAvailable.description')}
                  </Typography>
                </Box>
              }
            />
          </Grid2>
        ) : null}
        <Grid2 xs={12} lg={isTrialPlanDisplayed ? 4 : 6} sx={{ mt: { lg: -4, md: -2, xs: -2 }, mb: -2 }}>
          <PlanCard
            recommended
            title={t('plan.second.title')}
            subtitle={t('plan.second.subtitle')}
            countOfInvoices={40}
            countOfInvoicesText={t('countOfInvoicesText')}
            plansHeading={t('planIncludes')}
            noEmails={scenarioPlan.email}
            emailsDescription={t('plan.second.emailInfo', {
              count: scenarioPlan.email,
            })}
            noSms={'2-3'}
            smsDescription={t('plan.second.smsInfo_other')}
            noCalls={'...'}
            callsDescription={t('plan.second.phoneInfo_other')}
            disableSms={props.contact?.missingPhone}
            disableEmail={props.contact?.missingEmail}
            disableCalls={props.contact?.missingPhone}
            endOfRemindingDescription={t('plan.second.endOfRemindingInfo')}
            priceForUnit={`${getPriceWithCurrencyString(179, 'czk')} ${t('perMonth')}`}
            vatNote={t('vatNote')}
            actionContent={
              <Box
                flexDirection={'column'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={'100%'}
                pt={2}
                px={2}
              >
                {props.standardDiscount ? (
                  <Alert icon={<Discount />} severity={'success'} sx={{ mb: 2 }}>
                    {props.contact?.missingPhone ? t('plan.second.missingPhoneNumberDiscount') : null}
                    {props.contact?.missingEmail ? t('plan.second.missingEmailDiscount') : null}
                    {!props.contact?.missingEmail && !props.contact?.missingPhone ? t('plan.second.discount') : null}
                  </Alert>
                ) : null}
                <Button size={'large'} variant={'contained'} onClick={handleContactSales}>
                  {t('plan.second.action', {
                    amount: formatPrice(props.standardPrice),
                  })}
                </Button>
                <Typography mt={1} variant={'caption'} color={'textSecondary'} textAlign={'center'}>
                  {t('plan.second.overline')}
                </Typography>
              </Box>
            }
          />
        </Grid2>
        <Grid2 xs={12} lg={isTrialPlanDisplayed ? 4 : 6}>
          <PlanCard
            title={t('plan.third.title')}
            subtitle={t('plan.third.subtitle')}
            plansHeading={t('planMayInclude')}
            noEmails={3}
            emailsDescription={t('plan.third.emailInfo')}
            noSms={'2-3'}
            smsDescription={t('plan.third.smsInfo')}
            noCalls={'...'}
            callsDescription={t('plan.third.phoneInfo')}
            endOfRemindingDescription={t('plan.third.endOfRemindingInfo')}
            priceForUnit={t('plan.basedOnAgreement')}
            actionContent={
              <Box
                flexDirection={'column'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={'100%'}
                p={1}
              >
                <Box mb={2}>
                  <Typography sx={{ whiteSpace: 'break-spaces' }}>
                    <Trans
                      t={t}
                      i18nKey={'plan.third.contact'}
                      components={{
                        bold: <Typography variant={'subtitle1'} component={'span'} fontWeight={'bold'} />,
                        link1: <MUILink component={Link} to={`tel:${salesPhone}`} style={{ whiteSpace: 'nowrap' }} />,
                      }}
                      values={{
                        phoneNumber: formatPhoneNumber(salesPhone),
                      }}
                    />
                  </Typography>
                </Box>
                <Button size={'large'} variant={'outlined'} onClick={handleContactSales}>
                  {t('plan.third.action')}
                </Button>
                <Typography mt={1} variant={'caption'} color={'textSecondary'} textAlign={'center'}>
                  {t('plan.third.overline')}
                </Typography>
              </Box>
            }
          />{' '}
        </Grid2>
      </Grid2>
      <Box display={'flex'} mt={{ md: 6, xs: 2 }} justifyContent={'center'}>
        <Alert severity={'info'}>
          <Trans
            t={t}
            i18nKey={'alertInfo'}
            components={{
              link1: <MUILink component={Link} to={getConfig().planOfRemindingUrl} target={'_blank'} />,
            }}
          />
        </Alert>
      </Box>
    </Box>
  );
};
